import api from './api';

const authService = {
  /**
   * Login user
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{user, token}>}
   */
  async login(email, password) {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },

  /**
   * Register new organisation with admin user. Deliberately does not return
   * a session token - the account must be verified via email before it can
   * be used, so callers must send the user to login (not auto-authenticate).
   * @param {Object} organisation - {name, type}
   * @param {Object} user - {email, password, firstName, lastName}
   * @returns {Promise<{organisation, user}>}
   */
  async register(organisation, user) {
    const response = await api.post('/auth/register', { organisation, user });
    return response.data;
  },

  /**
   * Get current user profile
   * @returns {Promise<{user}>}
   */
  async getProfile() {
    const response = await api.get('/auth/me');
    return response.data;
  },

  /**
   * Verify email with token
   * @param {string} token
   * @returns {Promise<{message, user}>}
   */
  async verifyEmail(token) {
    const response = await api.post('/auth/verify-email', { token });
    return response.data;
  },

  /**
   * Resend verification email
   * @param {string} email
   * @returns {Promise<{message}>}
   */
  async resendVerification(email) {
    const response = await api.post('/auth/resend-verification', { email });
    return response.data;
  },

  /**
   * Request password reset
   * @param {string} email
   * @returns {Promise<{message}>}
   */
  async forgotPassword(email) {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  /**
   * Reset password with token
   * @param {string} token
   * @param {string} password
   * @returns {Promise<{message}>}
   */
  async resetPassword(token, password) {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },

  /**
   * Get all users (org admin only)
   * @returns {Promise<{users}>}
   */
  async getUsers() {
    const response = await api.get('/auth/users');
    return response.data;
  },

  /**
   * Create new user (org admin only)
   * @param {Object} userData
   * @returns {Promise<{user}>}
   */
  async createUser(userData) {
    const response = await api.post('/auth/users', userData);
    return response.data;
  },
};

export default authService;
